// Shake primitive — applies a brief camera position offset that decays
// exponentially. Hooks into the camera via useThree so it works regardless
// of how the camera was set up.
//
// Multiple concurrent shakes sum; the offset is applied additively each
// frame and reset before computing the next offset, so concurrent shakes
// compose without fighting each other.

import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';
import type { ShakeDef } from './types.js';

interface ShakeProps {
  readonly def: ShakeDef;
}

export function Shake({ def }: ShakeProps) {
  const camera = useThree((s) => s.camera);
  const startTimeRef = useRef(performance.now());
  const lastOffsetRef = useRef(new THREE.Vector3());

  useFrame(() => {
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    const t = elapsed / def.durationSec;
    if (t >= 1) {
      // Undo last offset and stop.
      camera.position.sub(lastOffsetRef.current);
      lastOffsetRef.current.set(0, 0, 0);
      return;
    }
    // Decay: e^(-3t) gives a quick falloff over the duration.
    const decay = Math.exp(-3 * t);
    const mag = def.intensity * decay;
    // Subtract last offset first so we don't accumulate.
    camera.position.sub(lastOffsetRef.current);
    const offset = new THREE.Vector3(
      (Math.random() - 0.5) * 2 * mag,
      (Math.random() - 0.5) * 2 * mag,
      (Math.random() - 0.5) * 2 * mag,
    );
    camera.position.add(offset);
    lastOffsetRef.current.copy(offset);
  });

  return null;
}
